import { CommonModule } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  OnInit,
  OnDestroy,
  Output,
  ViewChild,
} from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
  AbstractControl,
  ValidationErrors,
} from '@angular/forms';
import { Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { ApiService } from '../../shared/services/api.service';
import { secureStorage } from '../../shared/services/secure-storage.service';
import { CookieService } from '../../shared/services/cookie.service';
import { LoaderService } from '../../shared/services/loader.service';
import { BuyerVerificationService } from '../../shared/services/buyer-verification.service';
import { GstinItem } from '../../shared/models/buyer-verification.models';
import { ModalService } from '../../shared/services/modal.service';

@Component({
  selector: 'app-gst',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule],
  templateUrl: './gst.component.html',
  styleUrl: './gst.component.scss',
})
export class GstComponent implements OnInit, OnDestroy {
  @Output() close = new EventEmitter<void>();
  @Output() save = new EventEmitter<any>();
  showGstinSection = true; // or false
  isValid = false;
  sub: Subscription = new Subscription();
  gstForm: FormGroup;
  stateCode: string = '';
  gstins: GstinItem[] = [];
  gstinsLoading = false;
  gstinsError = '';
  gstinDropdownOpen = false;

  @ViewChild('gstinDropdown') gstinDropdown?: ElementRef<HTMLElement>;

  constructor(
    private apiService: ApiService,
    private fb: FormBuilder,
    private ssrcookieService: CookieService,
    private loaderService: LoaderService,
    private cdref: ChangeDetectorRef,
    private localstorage: secureStorage,
    private buyerVerificationService: BuyerVerificationService,
    private modalService: ModalService,
  ) {
    this.gstForm = this.fb.group({
      gst_number: [
        '',
        [
          Validators.required,
          Validators.minLength(15),
          Validators.maxLength(15),
          this.gstValidator,
        ],
      ],
      company_name: [''],
      address_1: [''],
      locality: [''],
      pincode: [
        '',
        [
          Validators.required,
          Validators.minLength(6),
          Validators.maxLength(6),
          Validators.pattern('^[0-9]*$'),
        ],
      ],
      city: [''],
      state: [''],
    });
    this.disableAllFieldsExceptGst();
  }

  /**
   * Custom validator for GST number
   * GST Format: 2 digits (state code) + 5 letters (PAN) + 4 digits (PAN) + 1 letter (PAN) + 1 entity number + Z + 1 checksum
   * Example: 22AAAAA0000A1Z5
   * Format breakdown:
   * - Positions 1-2: State Code (2 digits)
   * - Positions 3-7: First 5 chars of PAN (5 uppercase letters)
   * - Positions 8-11: Next 4 chars of PAN (4 digits)
   * - Position 12: Last char of PAN (1 uppercase letter)
   * - Position 13: Entity number (1-9 or A-Z)
   * - Position 14: Z (default)
   * - Position 15: Checksum (0-9 or A-Z)
   */
  gstValidator(control: AbstractControl): ValidationErrors | null {
    const value = control.value;

    if (!value) {
      return null; // Don't validate empty values (use Validators.required for that)
    }

    // GST number must be exactly 15 characters
    if (value.length !== 15) {
      return { invalidGst: { message: 'GSTIN must be exactly 15 characters' } };
    }

    // GST format regex pattern
    const gstPattern =
      /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/;

    if (!gstPattern.test(value.toUpperCase())) {
      return { invalidGst: { message: 'Please enter a valid GSTIN format' } };
    }

    return null; // Valid GST number
  }

  ngOnInit(): void {
    this.modalService.openModal('gst-popup', () => this.onCancel());
    this.loadGstins();

    this.gstForm
      .get('gst_number')
      ?.valueChanges.pipe(debounceTime(300), distinctUntilChanged())
      .subscribe((value) => {
        if (value && typeof value === 'string') {
          const upperValue = value.toUpperCase();
          if (value !== upperValue) {
            this.gstForm.patchValue(
              {
                gst_number: upperValue,
              },
              { emitEvent: false },
            );
          }
        }
      });
  }

  loadGstins(): void {
    this.gstinsLoading = true;
    this.gstinsError = '';
    this.sub.add(
      this.buyerVerificationService.getStatus().subscribe({
        next: (data) => {
          this.gstins = this.buyerVerificationService.filterGstDocuments(
            data.gstins,
          );
          if (this.gstins.length === 0 && data.pan_verified) {
            this.fetchLinkedGstins();
            return;
          }
          this.gstinsLoading = false;
          this.preselectGstin(data.selected_gstin);
          this.cdref.detectChanges();
        },
        error: (error: Error) => {
          this.gstinsLoading = false;
          this.gstinsError =
            error.message || 'Unable to load GSTINs. Please try again.';
          this.cdref.detectChanges();
        },
      }),
    );
  }

  private fetchLinkedGstins(): void {
    this.sub.add(
      this.buyerVerificationService.fetchGstins().subscribe({
        next: (data) => {
          this.gstins = this.buyerVerificationService.filterGstDocuments(
            data.gstins,
          );
          this.gstinsLoading = false;
          this.preselectGstin(data.selected_gstin);
          this.cdref.detectChanges();
        },
        error: (error: Error) => {
          this.gstinsLoading = false;
          this.gstinsError =
            error.message || 'Unable to load GSTINs. Please try again.';
          this.cdref.detectChanges();
        },
      }),
    );
  }

  private preselectGstin(selectedGstin?: string | null): void {
    const preferred =
      this.getGstinNumber(
        this.gstins.find((item) => this.getGstinNumber(item) === selectedGstin) ||
        this.gstins.find((item) => this.isGstinActive(item)),
      ) || '';
    if (!preferred) return;
    this.gstForm.patchValue({ gst_number: preferred }, { emitEvent: false });
    this.gstForm.get('gst_number')?.markAsDirty();
    this.getGstdata(preferred);
  }

  getGstinNumber(item: GstinItem | null | undefined): string {
    const row = (item || {}) as unknown as Record<string, unknown>;
    return String(row['document_number'] || row['gstin'] || '').trim();
  }

  getGstinStatus(item: GstinItem | null | undefined): string {
    const row = (item || {}) as unknown as Record<string, unknown>;
    return String(row['status'] || row['gstin_status'] || '').trim();
  }

  isGstinActive(item: GstinItem | null | undefined): boolean {
    return this.getGstinStatus(item).toLowerCase() === 'active';
  }

  get gstinTriggerLabel(): string {
    if (this.gstinsLoading) {
      return 'Loading GSTINs...';
    }
    const selected = this.gstForm.get('gst_number')?.value;
    if (selected) {
      return selected;
    }
    return this.gstins.length === 0 ? 'No GSTIN available' : 'Select GSTIN';
  }

  toggleGstinDropdown(): void {
    if (this.gstinsLoading || this.gstins.length === 0) {
      return;
    }
    this.gstinDropdownOpen = !this.gstinDropdownOpen;
  }

  isGstinSelected(item: GstinItem): boolean {
    return this.getGstinNumber(item) === this.gstForm.get('gst_number')?.value;
  }

  selectGstinFromList(item: GstinItem): void {
    if (!this.isGstinActive(item)) {
      return;
    }
    const gstNumber = this.getGstinNumber(item);
    this.gstForm.patchValue({ gst_number: gstNumber }, { emitEvent: false });
    this.gstForm.get('gst_number')?.markAsTouched();
    this.gstForm.get('gst_number')?.markAsDirty();
    this.gstinDropdownOpen = false;
    if (gstNumber && gstNumber.length === 15 && !this.gstForm.get('gst_number')?.errors) {
      this.getGstdata(gstNumber);
    }
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (
      this.gstinDropdownOpen &&
      this.gstinDropdown &&
      !this.gstinDropdown.nativeElement.contains(event.target as Node)
    ) {
      this.gstinDropdownOpen = false;
    }
  }

  onGstInput(event: any) {
    // Only handle uppercase conversion and cursor position
    const input = event.target;
    const start = input.selectionStart;
    const end = input.selectionEnd;
    const currentValue = this.gstForm.get('gst_number')?.value;

    // if (!currentValue || currentValue.length < 15) {
    //   this.enableAllFields();
    // }

    if (currentValue) {
      const upperValue = currentValue.toUpperCase();
      if (currentValue !== upperValue) {
        this.gstForm.patchValue(
          {
            gst_number: upperValue,
          },
          { emitEvent: false },
        );
        setTimeout(() => {
          input.setSelectionRange(start, end);
        }, 0);
      }
      // Do NOT call GST API here
    }
    // if(currentValue.length > 15){

    const gstControl = this.gstForm.get('gst_number');
    const gstNumber = gstControl?.value;
    gstControl?.markAsTouched();
    // Only fetch GST data if the number is valid
    if (gstNumber && gstNumber.length === 15 && !gstControl?.errors) {
      // Only call GST API on blur (not on input or valueChanges)
      this.getGstdata(gstNumber);
    }
  }

  //   onGstBlur() {
  //     const gstControl = this.gstForm.get('gst_number');
  //     const gstNumber = gstControl?.value;
  //     gstControl?.markAsTouched();
  //     // Only fetch GST data if the number is valid
  //     if (gstNumber && gstNumber.length === 15 && !gstControl?.errors) {
  //       // Only call GST API on blur (not on input or valueChanges)
  //       this.getGstdata(gstNumber);
  //     }
  // }

  onCancel(): void {
    this.modalService.closeModal('gst-popup');
    this.close.emit();
  }

  onClose(): void {
    this.modalService.closeModal('gst-popup');
    this.close.emit();
  }

  ngOnDestroy(): void {
    this.modalService.closeModal('gst-popup');
    this.sub.unsubscribe();
  }

  // onGstBlur() {
  //   const gstControl = this.gstForm.get('gst_number');
  //   const gstNumber = gstControl?.value;

  //   // Mark the field as touched to show validation errors
  //   gstControl?.markAsTouched();

  //   // Only fetch GST data if the number is valid
  //   if (gstNumber && gstNumber.length === 15 && !gstControl?.errors) {
  //     this.getGstdata(gstNumber);
  //   }
  // }

  /**
   * Get user-friendly error message for GST number field
   */
  getGstErrorMessage(): string {
    const gstControl = this.gstForm.get('gst_number');

    if (gstControl?.hasError('required')) {
      return 'GST number is required';
    }

    if (
      gstControl?.hasError('minlength') ||
      gstControl?.hasError('maxlength')
    ) {
      return 'GST number must be exactly 15 characters';
    }

    if (gstControl?.hasError('invalidGst')) {
      return (
        gstControl.errors?.['invalidGst']?.message ||
        'Invalid GST number format'
      );
    }

    return '';
  }

  getGstdata(number: string): void {
    this.loaderService.show();
    if (!number || number.length !== 15) {
      console.error('Invalid GST number');
      this.loaderService.hide();
      return;
    }

    const payload = {
      gst_number: number,
    };

    this.sub.add(
      this.apiService.getGstdata(payload).subscribe({
        next: (res) => {
          if (res && res.data) {
            // Patch pincode with emitEvent: false to avoid triggering valueChanges
            this.gstForm.patchValue(
              {
                gst_number: res.data.gst_number || '',
                company_name: res.data.company_name || '',
                address_1: res.data.address_1 || '',
                locality: res.data.locality || '',
                pincode: res.data.pincode || '',
              },
              { emitEvent: false },
            );

            // Disable all fields except GST number after successful API response
            this.disableAllFieldsExceptGst();

            // Only call onPincodeChange if pincode is present and city/state are not already filled
            const pincode = res.data.pincode || '';
            const city = this.gstForm.get('city')?.value;
            const state = this.gstForm.get('state')?.value;
            const stateCode = res.data.state_code || '';
            if (pincode) {
              this.onPincodeChange(pincode);
            }
          }
          this.cdref.detectChanges();
          this.loaderService.hide();
        },
        error: (err) => {
          console.error('Error fetching GST data:', err);
          this.gstForm.patchValue(
            {
              company_name: '',
              address_1: '',
              locality: '',
              pincode: '',
              city: '',
              state: '',
            },
            { emitEvent: false },
          );
          // Disable all fields except GST number after error
          this.disableAllFieldsExceptGst();
          // Mark form as pristine and untouched to disable save button
          this.gstForm.markAsPristine();
          this.gstForm.markAsUntouched();
          // Optional: show an error message to user via toastr/snackbar
          this.loaderService.hide();
        },
        complete: () => {
          this.loaderService.hide();
        },
      }),
    );
  }

  onPincodeChange(pincode: string): void {
    if (pincode && pincode.length === 6) {
      // Call API to get city and state based on pincode
      const payload = {
        dropdown_type: 'get_data_by_pincode',
        search_pincode: pincode,
      };

      this.sub.add(
        this.apiService.dataBypincode(payload).subscribe({
          next: (res: any) => {
            if (res.success && res.data) {
              const cityName = res.data.data_by_pincode[0].city_name;
              const stateName = res.data.data_by_pincode[0].state_name;
              this.stateCode = res.data.data_by_pincode[0].state_code || '';

              // this.selectedState = stateName;
              // this.selectedCity = cityName;
              // this.autoFilledCity = true;
              // this.autoFilledState = true;

              // this.states= [stateName]
              // this.cities= [cityName]

              this.gstForm.patchValue({
                city: cityName,
                state: stateName,
              });

              // Load cities for the selected state
              // this.loadCities(stateName);
            }
          },
          error: (err: any) => {
            console.error('Error fetching pincode details:', err);
          },
        }),
      );
    } else {
      this.gstForm.patchValue({
        city: '',
        state: '',
      });
    }
  }

  /**
   * Restricts input to alphanumeric characters only (for GST number input)
   */
  alphaNumberOnly(event: any) {
    var inp = String.fromCharCode(event.keyCode);

    // Allow only alphanumeric characters (no spaces for GST)
    const data = /[a-zA-Z0-9]/;

    if (data.test(inp)) {
      return true;
    } else {
      event.preventDefault();
      return false;
    }
  }

  /**
   * Converts GST number input to uppercase
   * Handles both typing and pasting
   */

  // Method to enable all form fields
  // enableAllFields(): void {
  //   this.gstForm.get('company_name')?.enable();
  //   this.gstForm.get('address_1')?.enable();
  //   this.gstForm.get('locality')?.enable();
  //   this.gstForm.get('pincode')?.enable();
  //   this.gstForm.get('city')?.enable();
  //   this.gstForm.get('state')?.enable();
  // }

  // Method to disable all form fields except GST number
  disableAllFieldsExceptGst(): void {
    this.gstForm.get('company_name')?.disable();
    this.gstForm.get('address_1')?.disable();
    this.gstForm.get('locality')?.disable();
    this.gstForm.get('pincode')?.disable();
    this.gstForm.get('city')?.disable();
    this.gstForm.get('state')?.disable();
  }

  addGstdata(): void {
    // Validate form before submission
    if (this.gstForm.invalid) {
      // Mark all fields as touched to show validation errors
      Object.keys(this.gstForm.controls).forEach((key) => {
        this.gstForm.get(key)?.markAsTouched();
      });
      console.error('Form is invalid');
      return;
    }

    this.loaderService.show();
    const payload = {
      address_1: this.gstForm.get('address_1')?.value,
      // "address_2": "",
      company_name: this.gstForm.get('company_name')?.value,
      // "address_3": "",
      state: this.gstForm.get('state')?.value,
      city: this.gstForm.get('city')?.value,
      land_mark: this.gstForm.get('locality')?.value,
      pincode: this.gstForm.get('pincode')?.value,
      // "phone_no": this.userData?.phone_number,
      is_gstin_no: true,
      gstin_number: this.gstForm.get('gst_number')?.value?.toUpperCase(),
      // "address_type": formData.saveAs,
      address_type_name: 'Billing Address',
      is_save_bill_default: true,
      state_code: this.stateCode,
      // "receiver_name": formData.receiverName,
      // "receiver_phone_no": formData.receiverPhone
    };

    this.sub.add(
      this.apiService.addGstdata(payload).subscribe({
        next: (res) => {
          if (res && res.data) {
            this.getUserDetails();
            this.save.emit(payload); // Emit the save event
            this.close.emit(); // Close the popup
          }
          this.loaderService.hide();
        },
        error: (err) => {
          console.error('Error adding GST data:', err);
          this.loaderService.hide();
        },
        complete: () => {
          this.loaderService.hide();
        },
      }),
    );
  }

  getUserDetails(): void {
    this.loaderService.show();
    const payload = {};
    this.sub.add(
      this.apiService.getUserDetails(payload).subscribe({
        next: (res) => {
          // Update localStorage and cookies with latest data
          localStorage.setItem('userData1', JSON.stringify(res?.data));
          this.ssrcookieService.setItem('eol_user', JSON.stringify(res?.data));
          this.loaderService.hide();
        },
        error: (err) => {
          console.error('Failed to fetch user details', err);
          this.loaderService.hide();
        },
        complete: () => {
          this.loaderService.hide();
        },
      }),
    );
  }

  ngOnDestory() {
    if (this.sub) {
      this.sub.unsubscribe();
    }
  }
}
